import React, { useState, useMemo, useCallback } from "react"
import {
  Row,
  Col,
  Form,
  Button,
  Table,
  Badge,
  Spinner,
} from "react-bootstrap"
import { Search, X, RefreshCw, AlertCircle, Phone, MapPin } from "react-feather"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"

function BrokerLedgerView({
  data = [],
  loading = false,
  fromDate,
  setFromDate,
  toDate,
  setToDate,
  selectedItemIds = [],
  openItemModal,
  onRefresh,
  onOpenDalali,
  onRowDoubleClick,
  setShowQtyDiffModal,
}) {
  const [brokerSearch, setBrokerSearch] = useState("")
  // totalFilter: Set containing ">0", "=0" or both
  const [totalFilter, setTotalFilter] = useState(new Set([">0", "=0"]))
  const [selectedRows, setSelectedRows] = useState(new Set())
  const [selectAll, setSelectAll] = useState(false)
  const [stationFilter, setStationFilter] = useState("all")

  // Extract unique stations for quick filter
  const uniqueStations = useMemo(() => {
    const set = new Set()
    data.forEach(r => {
      if (r.Station && r.Station.trim()) set.add(r.Station.trim())
    })
    return Array.from(set).sort()
  }, [data])

  // Filter and sort broker data
  const filteredData = useMemo(() => {
    let result = Array.isArray(data) ? [...data] : []

    // Search filter (Broker Name, Station, Person, Mobile)
    if (brokerSearch.trim()) {
      const q = brokerSearch.toLowerCase().trim()
      result = result.filter(row => {
        const name = (row.LedgerName || "").toLowerCase()
        const st = (row.Station || "").toLowerCase()
        const person = (row.Person || "").toLowerCase()
        const mob = (row.MobileNo || "").toLowerCase()
        return name.includes(q) || st.includes(q) || person.includes(q) || mob.includes(q)
      })
    }

    // Station filter
    if (stationFilter !== "all") {
      result = result.filter(row => (row.Station || "").trim() === stationFilter)
    }

    // Balance Dalali Filter (=0 and >0)
    if (totalFilter.size > 0 && totalFilter.size < 2) {
      result = result.filter(row => {
        const bal = parseFloat(row.BalanceDalali ?? row.Total ?? 0) || 0
        if (totalFilter.has(">0")) return bal > 0.001
        if (totalFilter.has("=0")) return Math.abs(bal) <= 0.001
        return true
      })
    } else if (totalFilter.size === 0) {
      result = []
    }

    // Sort alphabetically by Broker Name
    return result.sort((a, b) => {
      const nameA = (a.LedgerName || "").toLowerCase()
      const nameB = (b.LedgerName || "").toLowerCase()
      return nameA.localeCompare(nameB)
    })
  }, [data, brokerSearch, stationFilter, totalFilter])

  // Select all handler
  const handleSelectAll = useCallback((checked) => {
    setSelectAll(checked)
    if (checked) {
      setSelectedRows(new Set(filteredData.map(r => r.LedgerId)))
    } else {
      setSelectedRows(new Set())
    }
  }, [filteredData])

  // Single row select handler
  const handleRowSelect = useCallback((ledgerId, checked) => {
    setSelectedRows(prev => {
      const next = new Set(prev)
      if (checked) next.add(ledgerId)
      else next.delete(ledgerId)
      setSelectAll(filteredData.length > 0 && filteredData.every(r => next.has(r.LedgerId)))
      return next
    })
  }, [filteredData])

  // Calculate totals for selected rows (or all visible rows)
  const totals = useMemo(() => {
    const isSelection = selectedRows.size > 0
    const targetRows = isSelection
      ? filteredData.filter(r => selectedRows.has(r.LedgerId))
      : filteredData

    let totalBuyQty = 0
    let totalSellQty = 0
    let totalQty = 0
    let totalPayable = 0
    let totalPaid = 0
    let totalBalance = 0
    let totalDalaliRateWeighted = 0

    targetRows.forEach(row => {
      const bQty = parseFloat(row.BuyQty ?? 0) || 0
      const sQty = parseFloat(row.SellQty ?? 0) || 0
      const tQty = parseFloat(row.TotalQty ?? (bQty + sQty)) || 0
      const payable = parseFloat(row.TotalDalaliPayable ?? row.Total ?? 0) || 0
      const paid = parseFloat(row.DalaliPaid ?? 0) || 0
      const bal = parseFloat(row.BalanceDalali ?? (payable - paid)) || 0
      const rate = parseFloat(row.AvgDalaliRate ?? 0) || 0

      totalBuyQty += bQty
      totalSellQty += sQty
      totalQty += tQty
      totalPayable += payable
      totalPaid += paid
      totalBalance += bal
      totalDalaliRateWeighted += tQty * rate
    })

    const avgRate = totalQty > 0 ? totalDalaliRateWeighted / totalQty : 0

    return {
      isSelection,
      count: targetRows.length,
      totalCount: filteredData.length,
      totalBuyQty,
      totalSellQty,
      totalQty,
      avgRate,
      totalPayable,
      totalPaid,
      totalBalance,
    }
  }, [filteredData, selectedRows])

  return (
    <div className="broker-ledger-view d-flex flex-column h-100" style={{ minHeight: 0 }}>
      {/* DESKTOP KPI CARDS (Hidden on Mobile for screen space) */}
      <div className="d-none d-md-block bg-light p-2 border-bottom" style={{ flexShrink: 0 }}>
        <Row className="g-2 text-center">
          <Col md={2}>
            <div className="card shadow-sm border-0 py-1 px-2 h-100" style={{ background: "#ffffff", borderLeft: "4px solid #0d6efd" }}>
              <div style={{ fontSize: "0.68rem", color: "#64748b", fontWeight: "600" }}>Active Dalals</div>
              <div className="fw-bold text-primary" style={{ fontSize: "0.95rem" }}>
                {totals.isSelection ? `${totals.count}/${totals.totalCount}` : totals.totalCount}
              </div>
            </div>
          </Col>

          <Col md={2}>
            <div className="card shadow-sm border-0 py-1 px-2 h-100" style={{ background: "#ffffff", borderLeft: "4px solid #0284c7" }}>
              <div style={{ fontSize: "0.68rem", color: "#64748b", fontWeight: "600" }}>Total Trade Qty</div>
              <div className="fw-bold text-dark" style={{ fontSize: "0.95rem" }}>
                {totals.totalQty.toFixed(1)} <span style={{ fontSize: "0.65rem", color: "#64748b" }}>MT</span>
              </div>
            </div>
          </Col>

          <Col md={3}>
            <div className="card shadow-sm border-0 py-1 px-2 h-100" style={{ background: "#ffffff", borderLeft: "4px solid #dc2626" }}>
              <div style={{ fontSize: "0.68rem", color: "#64748b", fontWeight: "600" }}>Total Dalali Payable (Cr)</div>
              <div className="fw-bold text-danger" style={{ fontSize: "0.95rem" }}>
                ₹{totals.totalPayable.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
          </Col>

          <Col md={2}>
            <div className="card shadow-sm border-0 py-1 px-2 h-100" style={{ background: "#ffffff", borderLeft: "4px solid #16a34a" }}>
              <div style={{ fontSize: "0.68rem", color: "#64748b", fontWeight: "600" }}>Dalali Paid (Dr)</div>
              <div className="fw-bold text-success" style={{ fontSize: "0.95rem" }}>
                ₹{totals.totalPaid.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
          </Col>

          <Col md={3}>
            <div className="card shadow-sm border-0 py-1 px-2 h-100" style={{ background: totals.totalBalance > 0 ? "#fef2f2" : "#f0fdf4", borderLeft: `4px solid ${totals.totalBalance > 0 ? '#b91c1c' : '#15803d'}` }}>
              <div style={{ fontSize: "0.68rem", color: totals.totalBalance > 0 ? "#991b1b" : "#166534", fontWeight: "600" }}>
                Net Balance Dalali
              </div>
              <div className="fw-bold" style={{ fontSize: "0.95rem", color: totals.totalBalance > 0 ? "#b91c1c" : "#15803d" }}>
                ₹{totals.totalBalance.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
          </Col>
        </Row>
      </div>

      {/* MOBILE COMPACT KPI STRIP (Ultra-compact 2-tier pill strip so table gets max screen height) */}
      <div className="d-block d-md-none bg-light px-2 py-1 border-bottom" style={{ flexShrink: 0 }}>
        <div className="d-flex justify-content-between align-items-center mb-1">
          <div className="d-flex align-items-center gap-1">
            <span className="badge bg-primary" style={{ fontSize: "0.68rem" }}>
              Dalals: {totals.isSelection ? `${totals.count}/${totals.totalCount}` : totals.totalCount}
            </span>
            <span className="badge bg-dark" style={{ fontSize: "0.68rem" }}>
              Qty: {totals.totalQty.toFixed(1)} MT
            </span>
          </div>
          <span className="badge bg-danger" style={{ fontSize: "0.7rem", fontWeight: "bold" }}>
            Payable: ₹{totals.totalPayable.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
          </span>
        </div>
        <div className="d-flex justify-content-between align-items-center" style={{ fontSize: "0.7rem" }}>
          <span className="text-muted">Paid: <strong className="text-success">₹{totals.totalPaid.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</strong></span>
          <span className="fw-bold" style={{ color: totals.totalBalance > 0 ? "#b91c1c" : "#15803d" }}>
            Net Bal: ₹{totals.totalBalance.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
          </span>
        </div>
      </div>

      {/* FILTER BAR (Responsive Horizontal Scroll) */}
      <div className="bg-white px-2 py-1 border-bottom shadow-sm" style={{ flexShrink: 0 }}>
        <div className="d-flex align-items-center flex-wrap gap-2" style={{ overflowX: "auto" }}>
          {/* =0 and >0 Balance Dalali Toggle Filters */}
          <div
            className="d-flex align-items-center gap-2 px-2 py-1"
            style={{
              background: "#f8fafc",
              border: "1px solid #cbd5e1",
              borderRadius: "6px",
              height: "32px",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            <span style={{ fontSize: "0.72rem", fontWeight: "bold", color: "#475569" }}>Balance:</span>

            {/* >0 Outstanding Dalali */}
            <div className="form-check form-check-inline mb-0 d-flex align-items-center">
              <input
                type="checkbox"
                id="broker-filter-gt-zero"
                className="form-check-input checkbox-green"
                checked={totalFilter.has(">0")}
                onChange={() => {
                  setTotalFilter(prev => {
                    const next = new Set(prev)
                    if (next.has(">0")) next.delete(">0")
                    else next.add(">0")
                    return next
                  })
                }}
                style={{ cursor: "pointer", width: "15px", height: "15px" }}
              />
              <label
                htmlFor="broker-filter-gt-zero"
                className="form-check-label ms-1"
                style={{
                  fontSize: "0.72rem",
                  cursor: "pointer",
                  color: totalFilter.has(">0") ? "#15803d" : "#64748b",
                  fontWeight: totalFilter.has(">0") ? "bold" : "normal",
                  whiteSpace: "nowrap",
                }}
              >
                &gt; 0 (Outstanding)
              </label>
            </div>

            {/* =0 Settled / Nil Dalali */}
            <div className="form-check form-check-inline mb-0 d-flex align-items-center">
              <input
                type="checkbox"
                id="broker-filter-eq-zero"
                className="form-check-input checkbox-red"
                checked={totalFilter.has("=0")}
                onChange={() => {
                  setTotalFilter(prev => {
                    const next = new Set(prev)
                    if (next.has("=0")) next.delete("=0")
                    else next.add("=0")
                    return next
                  })
                }}
                style={{ cursor: "pointer", width: "15px", height: "15px" }}
              />
              <label
                htmlFor="broker-filter-eq-zero"
                className="form-check-label ms-1"
                style={{
                  fontSize: "0.72rem",
                  cursor: "pointer",
                  color: totalFilter.has("=0") ? "#dc2626" : "#64748b",
                  fontWeight: totalFilter.has("=0") ? "bold" : "normal",
                  whiteSpace: "nowrap",
                }}
              >
                = 0 (Settled)
              </label>
            </div>
          </div>

          {/* Search Broker Name / Station */}
          <div className="position-relative" style={{ minWidth: "160px", maxWidth: "240px", flex: "1 1 auto" }}>
            <Form.Control
              type="text"
              size="sm"
              placeholder="Search Dalal Name, Station..."
              value={brokerSearch}
              onChange={e => setBrokerSearch(e.target.value)}
              style={{
                height: "32px",
                fontSize: "0.75rem",
                paddingLeft: "26px",
                borderRadius: "6px",
                border: "1px solid #94a3b8",
              }}
            />
            <Search
              size={13}
              style={{ position: "absolute", left: "8px", top: "10px", color: "#64748b" }}
            />
            {brokerSearch && (
              <X
                size={13}
                onClick={() => setBrokerSearch("")}
                style={{ position: "absolute", right: "8px", top: "10px", color: "#94a3b8", cursor: "pointer" }}
              />
            )}
          </div>

          {/* Station Quick Filter */}
          {uniqueStations.length > 0 && (
            <div style={{ width: "120px", flexShrink: 0 }}>
              <Form.Select
                size="sm"
                value={stationFilter}
                onChange={e => setStationFilter(e.target.value)}
                style={{ height: "32px", fontSize: "0.73rem", borderRadius: "6px", border: "1px solid #94a3b8" }}
              >
                <option value="all">All Stations</option>
                {uniqueStations.map(st => (
                  <option key={st} value={st}>{st}</option>
                ))}
              </Form.Select>
            </div>
          )}

          {/* Date Picker Range */}
          <div className="d-flex align-items-center gap-1" style={{ whiteSpace: "nowrap", flexShrink: 0 }}>
            <div style={{ width: "95px" }}>
              <DatePicker
                selected={fromDate ? new Date(fromDate) : null}
                onChange={date => setFromDate(date)}
                className="form-control form-control-sm"
                dateFormat="dd/MM/yyyy"
                placeholderText="From Date"
                portalId="root-portal"
                popperPlacement="bottom-start"
                customInput={<input style={{ height: "32px", fontSize: "0.72rem", borderRadius: "6px" }} />}
              />
            </div>
            <span style={{ fontSize: "0.68rem", color: "#64748b", fontWeight: "600" }}>To</span>
            <div style={{ width: "95px" }}>
              <DatePicker
                selected={toDate ? new Date(toDate) : null}
                onChange={date => setToDate(date)}
                className="form-control form-control-sm"
                dateFormat="dd/MM/yyyy"
                placeholderText="To Date"
                portalId="root-portal"
                popperPlacement="bottom-start"
                customInput={<input style={{ height: "32px", fontSize: "0.72rem", borderRadius: "6px" }} />}
              />
            </div>
          </div>

          {/* Item Filter Button */}
          <Button
            variant="outline-primary"
            size="sm"
            onClick={openItemModal}
            style={{
              height: "32px",
              fontSize: "0.72rem",
              borderRadius: "6px",
              whiteSpace: "nowrap",
              fontWeight: "600",
              flexShrink: 0,
            }}
          >
            📦 Items {selectedItemIds && selectedItemIds.length > 0 ? `(${selectedItemIds.length})` : "(All)"}
          </Button>

          {/* Refresh Button */}
          <Button
            variant="light"
            size="sm"
            onClick={onRefresh}
            title="Refresh Broker Ledger"
            style={{ height: "32px", border: "1px solid #cbd5e1", borderRadius: "6px", flexShrink: 0 }}
          >
            <RefreshCw size={13} className={loading ? "spin" : ""} />
          </Button>
        </div>
      </div>

      {/* Main Table Area with dedicated scrolling */}
      <div className="table-scroll-container flex-grow-1" style={{ overflowY: "auto", overflowX: "auto" }}>
        {loading ? (
          <div className="text-center p-5">
            <Spinner animation="border" variant="primary" />
            <div className="mt-2 text-muted fw-semibold" style={{ fontSize: "0.85rem" }}>
              Loading Broker Dalali Ledger...
            </div>
          </div>
        ) : filteredData.length > 0 ? (
          <Table bordered hover size="sm" className="mb-0" style={{ minWidth: "1100px", tableLayout: "auto" }}>
            <thead className="table-primary text-center" style={{ position: "sticky", top: 0, zIndex: 10 }}>
              <tr>
                <th style={{ width: "42px", minWidth: "42px", whiteSpace: "nowrap" }}>
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={e => handleSelectAll(e.target.checked)}
                    className="form-check-input"
                    title="Select All Brokers"
                  />
                </th>
                <th style={{ minWidth: "220px", textAlign: "left", whiteSpace: "nowrap" }}>Broker (Dalal) Name</th>
                <th style={{ minWidth: "110px", textAlign: "left", whiteSpace: "nowrap" }}>Station</th>
                <th style={{ minWidth: "100px", textAlign: "right", whiteSpace: "nowrap" }}>Buy Qty (MT)</th>
                <th style={{ minWidth: "100px", textAlign: "right", whiteSpace: "nowrap" }}>Sell Qty (MT)</th>
                <th style={{ minWidth: "110px", textAlign: "right", whiteSpace: "nowrap" }}>Total Qty (MT)</th>
                <th style={{ minWidth: "110px", textAlign: "right", whiteSpace: "nowrap" }}>Avg Dalali Rate</th>
                <th style={{ minWidth: "130px", textAlign: "right", whiteSpace: "nowrap" }}>Dalali Payable (Cr)</th>
                <th style={{ minWidth: "120px", textAlign: "right", whiteSpace: "nowrap" }}>Dalali Paid (Dr)</th>
                <th style={{ minWidth: "130px", textAlign: "right", whiteSpace: "nowrap" }}>Balance Dalali</th>
                <th style={{ minWidth: "100px", textAlign: "center", whiteSpace: "nowrap" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.map(row => {
                const bal = parseFloat(row.BalanceDalali ?? (parseFloat(row.TotalDalaliPayable ?? 0) - parseFloat(row.DalaliPaid ?? 0))) || 0
                const isSelected = selectedRows.has(row.LedgerId)
                const rowBg = isSelected ? "#eff6ff" : (bal > 0 ? "#fefefe" : "#f8fafc")

                return (
                  <tr
                    key={`broker-${row.LedgerId}`}
                    onDoubleClick={() => onRowDoubleClick(row)}
                    style={{
                      cursor: "pointer",
                      backgroundColor: rowBg,
                      transition: "background-color 0.15s ease",
                    }}
                    title="Double-click to view ledger details"
                  >
                    <td className="text-center align-middle" onClick={e => e.stopPropagation()} style={{ whiteSpace: "nowrap" }}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={e => handleRowSelect(row.LedgerId, e.target.checked)}
                        className="form-check-input"
                      />
                    </td>
                    <td className="align-middle fw-semibold text-dark" style={{ whiteSpace: "nowrap" }}>
                      <div className="d-flex align-items-center justify-content-between">
                        <span>{row.LedgerName}</span>
                        {row.MobileNo && (
                          <span className="badge bg-light text-muted ms-1" style={{ fontSize: "0.65rem", fontWeight: "normal" }}>
                            <Phone size={10} className="me-1" />
                            {row.MobileNo}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="align-middle text-muted" style={{ whiteSpace: "nowrap" }}>
                      {row.Station ? (
                        <span><MapPin size={11} className="me-1 text-secondary" />{row.Station}</span>
                      ) : "-"}
                    </td>
                    <td className="align-middle text-end font-monospace" style={{ whiteSpace: "nowrap" }}>
                      {(parseFloat(row.BuyQty) || 0).toFixed(2)}
                    </td>
                    <td className="align-middle text-end font-monospace" style={{ whiteSpace: "nowrap" }}>
                      {(parseFloat(row.SellQty) || 0).toFixed(2)}
                    </td>
                    <td className="align-middle text-end font-monospace fw-bold text-primary" style={{ whiteSpace: "nowrap" }}>
                      {(parseFloat(row.TotalQty ?? ((parseFloat(row.BuyQty) || 0) + (parseFloat(row.SellQty) || 0)))).toFixed(2)}
                    </td>
                    <td className="align-middle text-end font-monospace text-muted" style={{ whiteSpace: "nowrap" }}>
                      ₹{(parseFloat(row.AvgDalaliRate) || 0).toFixed(2)}
                    </td>
                    <td className="align-middle text-end font-monospace fw-bold text-danger" style={{ whiteSpace: "nowrap" }}>
                      ₹{(parseFloat(row.TotalDalaliPayable ?? row.Total) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="align-middle text-end font-monospace fw-bold text-success" style={{ whiteSpace: "nowrap" }}>
                      ₹{(parseFloat(row.DalaliPaid) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="align-middle text-end font-monospace fw-bold" style={{ whiteSpace: "nowrap" }}>
                      <Badge
                        bg={bal > 0 ? "danger" : "success"}
                        style={{ fontSize: "0.74rem", padding: "4px 8px" }}
                      >
                        ₹{bal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Badge>
                    </td>
                    <td className="text-center align-middle" onClick={e => e.stopPropagation()} style={{ whiteSpace: "nowrap" }}>
                      <Button
                        variant="primary"
                        size="sm"
                        className="py-0 px-2 fw-semibold"
                        style={{ fontSize: "0.72rem", height: "24px" }}
                        onClick={() => onOpenDalali(row.LedgerId)}
                      >
                        Open Dalali
                      </Button>
                    </td>
                  </tr>
                )
              })}

              {/* Totals Row - Sticky bottom for Desktop */}
              <tr className="totals-row d-none d-md-table-row" style={{ backgroundColor: "#000000", color: "#ffffff", position: "sticky", bottom: 0, zIndex: 6 }}>
                <td className="text-center fw-bold" style={{ backgroundColor: "#000000", color: "#ffffff", whiteSpace: "nowrap" }}>
                  <span style={{ fontSize: "0.72rem" }}>
                    {totals.isSelection ? `${totals.count}/${totals.totalCount}` : totals.totalCount}
                  </span>
                </td>
                <td className="fw-bold" style={{ backgroundColor: "#000000", color: "#ffffff", whiteSpace: "nowrap" }}>
                  TOTAL ({totals.isSelection ? "Selected" : "All Brokers"})
                </td>
                <td style={{ backgroundColor: "#000000", color: "#ffffff", whiteSpace: "nowrap" }}>-</td>
                <td className="text-end fw-bold" style={{ backgroundColor: "#000000", color: "#ffffff", whiteSpace: "nowrap" }}>
                  <Badge bg="primary">{totals.totalBuyQty.toFixed(2)}</Badge>
                </td>
                <td className="text-end fw-bold" style={{ backgroundColor: "#000000", color: "#ffffff", whiteSpace: "nowrap" }}>
                  <Badge bg="info">{totals.totalSellQty.toFixed(2)}</Badge>
                </td>
                <td className="text-end fw-bold" style={{ backgroundColor: "#000000", color: "#ffffff", whiteSpace: "nowrap" }}>
                  <Badge bg="light" text="dark">{totals.totalQty.toFixed(2)} MT</Badge>
                </td>
                <td className="text-end fw-bold" style={{ backgroundColor: "#000000", color: "#ffffff", whiteSpace: "nowrap" }}>
                  <span style={{ fontSize: "0.75rem", color: "#93c5fd" }}>₹{totals.avgRate.toFixed(2)}</span>
                </td>
                <td className="text-end fw-bold" style={{ backgroundColor: "#000000", color: "#ffffff", whiteSpace: "nowrap" }}>
                  <Badge bg="danger">₹{totals.totalPayable.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Badge>
                </td>
                <td className="text-end fw-bold" style={{ backgroundColor: "#000000", color: "#ffffff", whiteSpace: "nowrap" }}>
                  <Badge bg="success">₹{totals.totalPaid.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Badge>
                </td>
                <td className="text-end fw-bold" style={{ backgroundColor: "#000000", color: "#ffffff", whiteSpace: "nowrap" }}>
                  <Badge bg={totals.totalBalance > 0 ? "danger" : "success"}>
                    ₹{totals.totalBalance.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Badge>
                </td>
                <td style={{ backgroundColor: "#000000", whiteSpace: "nowrap" }}>-</td>
              </tr>
            </tbody>
          </Table>
        ) : (
          <div className="text-center p-5 text-muted">
            <AlertCircle size={40} className="mb-2 text-warning" />
            <h6 className="fw-bold">No Broker Dalali Records Found</h6>
            <p style={{ fontSize: "0.8rem" }}>
              Try checking `=0 (Settled)` or `&gt; 0 (Outstanding)` filters above, or adjust the date range.
            </p>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => {
                setBrokerSearch("")
                setTotalFilter(new Set([">0", "=0"]))
                setStationFilter("all")
              }}
            >
              Reset Filters
            </Button>
          </div>
        )}
      </div>

      {/* Mobile Sticky Bottom Summary Bar (Positioned above footer with crisp totals) */}
      <div
        className="d-block d-md-none"
        style={{
          flexShrink: 0,
          backgroundColor: "#0f172a",
          borderTop: "2px solid #3b82f6",
          padding: "5px 8px",
          color: "#ffffff",
          zIndex: 25,
        }}
      >
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-1">
          <div className="d-flex align-items-center gap-1">
            <span className="badge bg-primary" style={{ fontSize: "0.68rem" }}>
              Qty: {totals.totalQty.toFixed(1)} MT
            </span>
            <span className="badge bg-danger" style={{ fontSize: "0.68rem" }}>
              Pay: ₹{totals.totalPayable.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
            </span>
          </div>
          <div className="d-flex align-items-center gap-1">
            <span className="badge bg-success" style={{ fontSize: "0.68rem" }}>
              Paid: ₹{totals.totalPaid.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
            </span>
            <span
              className={`badge ${totals.totalBalance > 0 ? 'bg-danger' : 'bg-success'}`}
              style={{ fontSize: "0.72rem", fontWeight: "bold" }}
            >
              Bal: ₹{totals.totalBalance.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default BrokerLedgerView
